import React from "react";
import { Box, Card, Chip, CircularProgress, Sheet, Stack, Table, Typography } from "@mui/joy";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  CasinoIcon,
  CheckIcon,
  CloseIcon,
  EmailIcon,
  EventIcon,
  PeopleIcon,
  SDelete,
} from "../../../styles/StyledMUI";
import {
  colorTokens,
  hrMobileRequestCardSx,
  hrSheetSx,
  hrTableSx,
  textTokens,
} from "../../../styles/globalStyles";
import CommonModal from "../../common/CommonModal";
import DarkTypography from "../../common/DarkTypography";
import IconButtonCTA from "../../common/IconButtonCTA";
import RandomDrawer from "../../common/RandomDrawer";
import { useEvent } from "../../../contexts/api";
import { useConfirmationModal } from "../../../contexts/app/confirmation";
import type {
  Event,
  EventParticipant,
  UpdateEventRequest,
  Winner,
} from "../../../api/events/Event.typed";

type Props = {
  open: boolean;
  onClose: () => void;
  event: Event;
  canEdit: boolean;
  onUpdateEvent: (req: UpdateEventRequest) => Promise<unknown>;
  onRefresh: () => Promise<void>;
};

const participantsScrollSx = {
  ...hrSheetSx,
  maxHeight: "min(38dvh, 380px)",
  overflow: "auto",
  overscrollBehavior: "contain" as const,
};

const winnersScrollSx = {
  ...hrSheetSx,
  maxHeight: "min(22dvh, 220px)",
  overflow: "auto",
  overscrollBehavior: "contain" as const,
};

const metaItemSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.25,
  opacity: 0.8,
};

const metaIconSx = { fontSize: "0.8rem", opacity: 0.7 };

const metaDotSx = {
  display: "inline-block",
  width: 3,
  height: 3,
  borderRadius: "50%",
  bgcolor: textTokens.color.faint,
  opacity: 0.6,
  flexShrink: 0,
};

function ConsentBadge({ ok, label }: { ok: boolean; label: string }) {
  const Icon = ok ? CheckIcon : CloseIcon;
  const color = ok ? "var(--joy-palette-success-400)" : "var(--joy-palette-danger-400)";

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <Icon sx={{ fontSize: "0.85rem", color }} />
      <Typography level="body-xs" sx={{ opacity: 0.75, lineHeight: 1 }}>
        {label}
      </Typography>
    </Box>
  );
}

function ParticipantCard({
  participant,
  canEdit,
  onDelete,
}: {
  participant: EventParticipant;
  canEdit: boolean;
  onDelete: (p: EventParticipant) => void;
}) {
  return (
    <Card
      sx={{
        ...hrMobileRequestCardSx,
        mb: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "visible",
        gap: 0.4,
        p: { xs: 0.75, sm: 1 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <DarkTypography
          level="body-sm"
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: "bold",
            fontSize: "0.85rem",
            wordBreak: "break-word",
            lineHeight: 1.2,
          }}
        >
          {participant.first_name} {participant.last_name}
        </DarkTypography>
        {canEdit && (
          <IconButtonCTA
            size="sm"
            variant="plain"
            ctaVariant="danger"
            aria-label="Retirer le participant"
            onClick={() => onDelete(participant)}
            sx={{ flexShrink: 0, "--IconButton-size": "26px", m: -0.25 }}
          >
            <SDelete />
          </IconButtonCTA>
        )}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
        <EmailIcon sx={{ fontSize: "0.8rem", opacity: 0.55, flexShrink: 0 }} />
        <Typography level="body-xs" sx={{ wordBreak: "break-all", opacity: 0.85, lineHeight: 1.2 }}>
          {participant.email}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 0.6,
          rowGap: 0.35,
        }}
      >
        <Box sx={metaItemSx}>
          <EventIcon sx={metaIconSx} />
          <Typography level="body-xs" sx={{ lineHeight: 1 }}>
            {format(new Date(participant.created_at), "dd/MM/yy", { locale: fr })}
          </Typography>
        </Box>
        <Box sx={metaDotSx} />
        <Typography level="body-xs" sx={{ ...metaItemSx, fontWeight: "md" }}>
          {participant.votes} bull.
        </Typography>
        <Box sx={metaDotSx} />
        <ConsentBadge ok={participant.terms_consent} label="CGU" />
        <ConsentBadge ok={participant.newsletter_consent} label="NL" />
      </Box>
    </Card>
  );
}

function WinnerCard({
  winner,
  canEdit,
  onDelete,
}: {
  winner: Winner;
  canEdit: boolean;
  onDelete: (id: number) => void;
}) {
  return (
    <Card
      sx={{
        ...hrMobileRequestCardSx,
        mb: 0,
        minWidth: 0,
        maxWidth: "100%",
        overflow: "visible",
        p: { xs: 0.6, sm: 0.85 },
        flexDirection: "row",
        alignItems: "center",
        gap: 0.6,
      }}
    >
      <Chip
        size="sm"
        variant="soft"
        color="warning"
        sx={{ flexShrink: 0, "--Chip-minHeight": "20px", px: 0.5 }}
      >
        {winner.rank}e
      </Chip>
      <DarkTypography
        level="body-xs"
        sx={{
          fontWeight: "bold",
          flex: 1,
          minWidth: 0,
          wordBreak: "break-word",
          lineHeight: 1.2,
        }}
      >
        {winner.first_name} {winner.last_name}
      </DarkTypography>
      {canEdit && (
        <IconButtonCTA
          size="sm"
          variant="plain"
          ctaVariant="danger"
          aria-label="Retirer le gagnant"
          onClick={() => onDelete(winner.id)}
          sx={{ flexShrink: 0, "--IconButton-size": "24px", m: -0.25 }}
        >
          <SDelete />
        </IconButtonCTA>
      )}
    </Card>
  );
}

export default function EventParticipantsModal({
  open,
  onClose,
  event,
  canEdit,
  onUpdateEvent,
  onRefresh,
}: Props) {
  const { getEventParticipants, deleteEventParticipant } = useEvent();
  const { showConfirmation } = useConfirmationModal();
  const [participants, setParticipants] = React.useState<EventParticipant[]>([]);
  const [loadingPart, setLoadingPart] = React.useState(false);
  const [showDraw, setShowDraw] = React.useState(false);

  const refreshParticipants = React.useCallback(async () => {
    setLoadingPart(true);
    const res = await getEventParticipants(event.id);
    setParticipants(res ?? []);
    setLoadingPart(false);
  }, [event.id, getEventParticipants]);

  React.useEffect(() => {
    if (!open) return;
    refreshParticipants();
  }, [open, refreshParticipants]);

  const handleDeleteParticipant = async (participant: EventParticipant) => {
    const ok = await showConfirmation(
      "Supprimer le participant",
      `Retirer ${participant.first_name} ${participant.last_name} de l'événement ?`,
    );
    if (!ok) return;
    await deleteEventParticipant(event.id, participant.id);
    await refreshParticipants();
  };

  const handleDeleteWinner = async (winnerId: number) => {
    const ok = await showConfirmation("Retirer le gagnant", "Retirer ce gagnant du classement ?");
    if (!ok) return;
    const updatedWinners = event.winners
      .filter((w) => w.id !== winnerId)
      .map((w, idx) => ({ ...w, rank: idx + 1 }));
    await onUpdateEvent({
      id: event.id,
      name: event.name,
      description: event.description,
      start_date: event.start_date,
      end_date: event.end_date,
      winners: updatedWinners,
    });
    await onRefresh();
  };

  const handleDrawRefresh = async () => {
    await onRefresh();
  };

  const footerActions = canEdit ? (
    <IconButtonCTA
      ctaVariant="accent"
      aria-label="Tirage au sort"
      size="sm"
      disabled={participants.length === 0}
      onClick={() => setShowDraw(true)}
    >
      <CasinoIcon sx={{ fontSize: "1.1rem" }} />
    </IconButtonCTA>
  ) : undefined;

  return (
    <>
      <CommonModal
        open={open}
        onClose={onClose}
        title="Participants"
        subtitle={event.name}
        size="xl"
        fitContentHeight
        footerActions={footerActions}
      >
        <Stack spacing={1.5} sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
              <PeopleIcon sx={{ fontSize: "1rem", color: colorTokens.text.title }} />
              <DarkTypography level="title-sm" colorVariant="title">
                {loadingPart ? "Chargement…" : `${participants.length} participant(s)`}
              </DarkTypography>
            </Box>

            {loadingPart ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : participants.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <PeopleIcon sx={{ fontSize: "1.75rem", color: textTokens.color.muted, mb: 0.5 }} />
                <DarkTypography level="body-sm" colorVariant="muted">
                  Aucun participant inscrit
                </DarkTypography>
              </Box>
            ) : (
              <>
                {/* Desktop: table with horizontal scroll fallback. */}
                <Sheet
                  sx={{
                    ...participantsScrollSx,
                    display: { xs: "none", md: "block" },
                  }}
                >
                  <Table hoverRow stickyHeader size="sm" sx={hrTableSx}>
                    <thead>
                      <tr>
                        <th style={{ minWidth: 130 }}>Nom</th>
                        <th style={{ minWidth: 200 }}>Email</th>
                        <th style={{ width: 130 }}>Consentements</th>
                        <th style={{ width: 80, textAlign: "center" }}>Bulletins</th>
                        <th style={{ width: 110 }}>Inscription</th>
                        {canEdit && <th style={{ width: 56 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {participants.map((p) => (
                        <tr key={p.id}>
                          <td>
                            <Typography level="body-xs" fontWeight="md">
                              {p.first_name} {p.last_name}
                            </Typography>
                          </td>
                          <td>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <EmailIcon sx={{ fontSize: "0.85rem", opacity: 0.5 }} />
                              <Typography
                                level="body-xs"
                                sx={{ wordBreak: "break-all", opacity: 0.8 }}
                              >
                                {p.email}
                              </Typography>
                            </Box>
                          </td>
                          <td>
                            <Stack direction="row" spacing={0.75}>
                              <ConsentBadge ok={p.terms_consent} label="CGU" />
                              <ConsentBadge ok={p.newsletter_consent} label="NL" />
                            </Stack>
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <Typography level="body-xs" fontWeight="md">
                              {p.votes}
                            </Typography>
                          </td>
                          <td>
                            <Typography level="body-xs" sx={{ opacity: 0.7 }}>
                              {format(new Date(p.created_at), "dd/MM/yyyy", { locale: fr })}
                            </Typography>
                          </td>
                          {canEdit && (
                            <td>
                              <IconButtonCTA
                                size="sm"
                                variant="plain"
                                ctaVariant="danger"
                                aria-label="Retirer le participant"
                                onClick={() => handleDeleteParticipant(p)}
                              >
                                <SDelete />
                              </IconButtonCTA>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Sheet>

                {/* Mobile/tablet: stacked cards. */}
                <Box
                  sx={{
                    display: { xs: "flex", md: "none" },
                    flexDirection: "column",
                    gap: 0.5,
                    maxHeight: "min(58dvh, 500px)",
                    overflow: "auto",
                    overscrollBehavior: "contain",
                    pr: 0.5,
                    minWidth: 0,
                  }}
                >
                  {participants.map((p) => (
                    <ParticipantCard
                      key={p.id}
                      participant={p}
                      canEdit={canEdit}
                      onDelete={handleDeleteParticipant}
                    />
                  ))}
                </Box>
              </>
            )}
          </Box>

          {event.winners && event.winners.length > 0 && (
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                <CasinoIcon sx={{ fontSize: "1rem", color: colorTokens.text.title }} />
                <DarkTypography level="title-sm" colorVariant="title">
                  Gagnants ({event.winners.length})
                </DarkTypography>
              </Box>

              {/* Desktop winners table. */}
              <Sheet
                sx={{
                  ...winnersScrollSx,
                  display: { xs: "none", md: "block" },
                }}
              >
                <Table hoverRow stickyHeader size="sm" sx={hrTableSx}>
                  <thead>
                    <tr>
                      <th style={{ width: 70, textAlign: "center" }}>Place</th>
                      <th>Nom</th>
                      {canEdit && <th style={{ width: 56 }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {event.winners.map((winner) => (
                      <tr key={winner.id}>
                        <td style={{ textAlign: "center" }}>
                          <Chip size="sm" variant="soft" color="warning">
                            {winner.rank}e
                          </Chip>
                        </td>
                        <td>
                          <Typography level="body-xs" fontWeight="md">
                            {winner.first_name} {winner.last_name}
                          </Typography>
                        </td>
                        {canEdit && (
                          <td>
                            <IconButtonCTA
                              size="sm"
                              variant="plain"
                              ctaVariant="danger"
                              aria-label="Retirer le gagnant"
                              onClick={() => handleDeleteWinner(winner.id)}
                            >
                              <SDelete />
                            </IconButtonCTA>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Sheet>

              {/* Mobile/tablet winners cards. */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  flexDirection: "column",
                  gap: 0.4,
                  maxHeight: "min(30dvh, 260px)",
                  overflow: "auto",
                  overscrollBehavior: "contain",
                  pr: 0.5,
                  minWidth: 0,
                }}
              >
                {event.winners.map((winner) => (
                  <WinnerCard
                    key={winner.id}
                    winner={winner}
                    canEdit={canEdit}
                    onDelete={handleDeleteWinner}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Stack>
      </CommonModal>

      <RandomDrawer
        open={showDraw}
        onClose={() => setShowDraw(false)}
        participants={participants.map((p) => ({
          id: p.id,
          name: `${p.first_name} ${p.last_name}`,
          votes: p.votes,
        }))}
        winners={event.winners}
        event={event}
        onRefresh={handleDrawRefresh}
      />
    </>
  );
}
